import { useCallback, useContext, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CARRIER_OMS_MYORDERS, SHIPPER_OMS_MYORDERS, orderDetailsPath } from '@/constants/routes';
import { UserContext } from '@/contexts/UserContext';
import { toArrayLiteral } from '@/utilities/toArrayLiteral';
import { EXCEPTION_TYPES } from '@onward-delivery/core';
import { useQuery } from '@apollo/client';
import { captureException } from '@sentry/react';
import { SPLIT_CHILDREN } from './queries';

export const useExceptionCallbacks = (
    { order, exceptionParams, route_id },
    { insertExceptionEvent, insertException, updateException }
) => {
    const createException = useCallback(
        (exception) => {
            const { __typename, items, images, mappings, ...rest } = exception;

            if (exceptionParams.action) {
                const attemptMappingIdsFromParams = Array.isArray(exceptionParams.attempt_mapping_ids)
                    ? exceptionParams.attempt_mapping_ids.filter(Boolean)
                    : [];
                const fallbackAttemptMappingIds = (order?.routes || [])
                    .filter(
                        (mapping) =>
                            mapping?.mapping_id &&
                            mapping?.route_id === route_id &&
                            (mapping?.duplicate_override == null || mapping?.duplicate_override === 'DISCARD')
                    )
                    .map((mapping) => mapping.mapping_id);
                const attemptMappingIds = exceptionParams.action.includes('ATTEMPT')
                    ? attemptMappingIdsFromParams.length > 0
                        ? attemptMappingIdsFromParams
                        : fallbackAttemptMappingIds
                    : [];

                const orderUpdates = exceptionParams.orderUpdates || {
                    ltl_dropoff_arrival: null,
                    completion_time: null,
                    loading_completed: null,
                    ltl_pickup_completed: null,
                    del_window_start: null,
                    del_window_end: null,
                    delivery_time_confirmed: null,
                    confirmation_source: null,
                    order_status: 'claimed',
                };
                // if ([EXCEPTION_TYPES.SHORTAGE, EXCEPTION_TYPES.DAMAGED, 'itemsTooDamaged'].includes(rest.type)) {
                //     orderUpdates.order_status = 'on_hold';
                // }
                const selectedItemIds = items
                    ? Array.from(
                          new Set(
                              Object.values(items)
                                  .map((item) => item?.item_id)
                                  .filter((item_id) => !!item_id)
                          )
                      )
                    : [];

                return insertExceptionEvent({
                    variables: {
                        exception_event: {
                            ev_action: exceptionParams.action,
                            ev_status: 'NOT_DELIVERED',
                            ex_call_or_text: rest.call_or_text,
                            ex_images: toArrayLiteral(images || []),
                            ex_instruction: rest.instruction,
                            ex_notes: rest.notes,
                            ex_reason: rest.reason,
                            ex_reported_at: rest.reported_at,
                            ex_resolution: rest.resolution,
                            ex_ring_or_knock: rest.ring_or_knock,
                            ex_route_id: route_id,
                            ex_type: rest.type,
                            ex_wait_time: rest.wait_time,
                            mapping_exception_type: items ? 'ITEM' : 'ORDER',
                            mapping_items: toArrayLiteral(selectedItemIds),
                            mapping_order: order.order_id,
                        },
                        order: {
                            order_id: order.order_id,
                            ...orderUpdates,
                        },
                        order_update_cols: Object.keys(orderUpdates),
                        attempt_mapping_ids: attemptMappingIds,
                    },
                });
            } else if (rest.exception_id) {
                const { order_id: existingOrderId, event_id } = mappings?.[0] || {};

                const selectedItemIds = new Set(
                    Object.values(items || {})
                        .map((item) => item?.item_id)
                        .filter((id) => !!id)
                );

                const existingItemIds = new Set((mappings || []).map((m) => m.item_id).filter((id) => !!id));
                const hasOrderMapping = (mappings || []).some((m) => !m.item_id);

                let mappingsToCreate = [];
                let mappingsToDelete = [];

                if (selectedItemIds.size > 0) {
                    const toCreateIds = Array.from(selectedItemIds).filter((id) => !existingItemIds.has(id));
                    mappingsToCreate = toCreateIds.map((item_id) => ({
                        item_id,
                        exception_id: rest.exception_id,
                        exception_type: 'ITEM',
                        order_id: existingOrderId || order.order_id,
                        event_id: event_id,
                    }));

                    mappingsToDelete = (mappings || [])
                        .filter((m) => !m.item_id || !selectedItemIds.has(m.item_id))
                        .map((m) => m.mapping_id);
                } else {
                    if (!hasOrderMapping) {
                        mappingsToCreate = [
                            {
                                exception_id: rest.exception_id,
                                exception_type: 'ORDER',
                                order_id: existingOrderId || order.order_id,
                                event_id: event_id,
                            },
                        ];
                    }
                    mappingsToDelete = (mappings || []).filter((m) => !!m.item_id).map((m) => m.mapping_id);
                }

                return updateException({
                    variables: {
                        exception_id: rest.exception_id,
                        exception: {
                            ...rest,
                            images: toArrayLiteral(images || []),
                        },
                        mappingsToCreate,
                        mappingsToDelete,
                    },
                });
            } else {
                const itemValues = items ? Object.values(items) : [];
                const uniqueItemIds = Array.from(new Set(itemValues.map((i) => i?.item_id).filter((id) => !!id)));
                return insertException({
                    variables: {
                        exception: {
                            ...rest,
                            images: toArrayLiteral(images || []),
                            mappings: {
                                data:
                                    items && uniqueItemIds.length > 0
                                        ? uniqueItemIds.map((item_id) => ({
                                              exception_type: 'ITEM',
                                              order_id: order.order_id,
                                              item_id,
                                          }))
                                        : [
                                              {
                                                  exception_type: 'ORDER',
                                                  order_id: order.order_id,
                                              },
                                          ],
                            },
                        },
                    },
                });
            }
        },
        [order, exceptionParams]
    );

    return { createException };
};

// Stable empty result so memos that depend on the children don't rerun every render.
const NO_CHILDREN = [];

/**
 * Reads a split parent's children for the children panel. Does nothing unless `skip` is false.
 *
 * @param {{ parentId?: string, skip?: boolean }} args `skip` should be true unless the order is a split parent.
 * @returns {{ children: object[], loading: boolean, error: Error|undefined, refetch: Function }} Children in `split_seq` order.
 */
export const useSplitChildren = ({ parentId, skip }) => {
    const { data, loading, error, refetch } = useQuery(SPLIT_CHILDREN, {
        variables: { parent_id: parentId },
        skip: skip || !parentId,
        fetchPolicy: 'cache-and-network',
    });
    useEffect(() => {
        if (error) captureException(error);
    }, [error]);
    return { children: data?.orders ?? NO_CHILDREN, loading: loading && !data, error, refetch };
};

// The list a tab that started on an order was opened from (My Orders opens each order in a new tab, which sets
// document.referrer to the list's URL), or null when the referrer is missing, another site, or another order page.
const openerListPath = () => {
    try {
        const url = new URL(document.referrer);
        if (url.origin !== window.location.origin || /^\/(order|job)\//.test(url.pathname)) return null;
        return url.pathname + url.search + url.hash;
    } catch {
        return null;
    }
};

/**
 * Moves between linked orders (parent, split children, clones) without losing the way back to the list the user came
 * from. Each order-to-order move adds a history entry, so a plain `navigate(-1)` from a child lands on its parent. The
 * hop count rides in router state, and `goBack` skips those entries. Any other state, such as `source`, carries over.
 *
 * When the first order was the first page in its tab (opened in a new tab from My Orders, or from a pasted link), there
 * is no list behind it in history. `goBack` then opens the list that opened the tab, or the account's My Orders.
 *
 * @returns {{ linkState: object, goToOrder: (orderId: string) => void, goBack: (steps?: number) => void }}
 *   `linkState` goes on a `<Link state>` to another order. `goToOrder` navigates there with it. `goBack(steps)` goes
 *   back `steps` entries (default 1) from where the user first opened an order.
 */
export const useLinkedOrderNavigation = () => {
    const location = useLocation();
    const navigate = useNavigate();
    // A shipper account's list is the shipper one; every other account uses the carrier one, as at sign-in (Auth.js).
    const isShipper = !!useContext(UserContext)?.client?.user?.roles?.SHIPPER;
    const hops = location.state?.linkedOrderHops ?? 0;
    // React Router keys a tab's first entry 'default'; after a hop, the first order's answer rides in state.
    const startedTab = hops === 0 ? location.key === 'default' : !!location.state?.linkedOrderStartedTab;
    const linkState = useMemo(
        () => ({ ...location.state, linkedOrderHops: hops + 1, linkedOrderStartedTab: startedTab }),
        [location.state, hops, startedTab]
    );

    const goToOrder = useCallback(
        (orderId) => navigate(orderDetailsPath(orderId), { state: linkState }),
        [navigate, linkState]
    );
    const goBack = useCallback(
        (steps = 1) => {
            if (!startedTab) {
                navigate(-(hops + steps));
                return;
            }
            navigate(
                openerListPath() || (isShipper ? SHIPPER_OMS_MYORDERS : CARRIER_OMS_MYORDERS)
            );
        },
        [navigate, hops, startedTab, isShipper]
    );

    return { linkState, goToOrder, goBack };
};
